// Copyright (c) Ensho Health, 2021 to present

import forge from 'node-forge';
import scrypt from 'scrypt-js';
import seedrandom from 'seedrandom';

import { stringToArray } from './string-buffer.js';
import b16to64url from '../b16to64url.js';
import { removeKey, storeKey, getKey } from './index-db-utils.js';

class Hspii {
  constructor(options = { siteSalt: null, cryptoParameters: null }) {
    this.siteSalt = options.siteSalt;
    this.cryptoParameters = options.cryptoParameters;
    this.publicKey = null;
    this.privateKey = null;
  }

  setPrivateKey(key) {
    this.privateKey = key;
  }

  setSiteSalt(salt) {
    this.siteSalt = salt;
  }

  setCryptoParameters(parameters) {
    this.cryptoParameters = parameters;
  }

  async generateRSAKeys(password, onProgress) {
    try {
      if (this.siteSalt && this.cryptoParameters) {
        const arrayPwd = stringToArray(password);
        const arraySalt = stringToArray(this.siteSalt);

        const passphrase = await scrypt.scrypt(
          arrayPwd,
          arraySalt,
          this.cryptoParameters.scryptN,
          this.cryptoParameters.scryptR,
          this.cryptoParameters.scryptP,
          this.cryptoParameters.scryptDklen,
          onProgress
        );

        // Setup and initialize the seeded PRNG used in RSA key generation
        const seededPRNG = seedrandom(passphrase);

        const myPrng = {
          getBytesSync(count) {
            const b = forge.util.createBuffer();
            while (b.length() < count) {
              b.putByte(Math.floor(seededPRNG() * 256));
            }
            return b.getBytes(count);
          }
        };

        // Generate the RSA key pair
        const { publicKey, privateKey } = forge.pki.rsa.generateKeyPair({
          bits: this.cryptoParameters.RSABits,
          e: this.cryptoParameters.RSAExponent,
          prng: myPrng
        });

        // Import the forge keys into Web Crypto API CryptoKey objects.
        const n = b16to64url(publicKey.n.toString(16));
        const e = b16to64url(publicKey.e.toString(16));

        const format = 'jwk';

        const publicKeyCrypto = await crypto.subtle.importKey(
          format,
          {
            e,
            kty: 'RSA',
            n
          },
          {
            hash: 'SHA-256',
            modulusLength: this.cryptoParameters.RSABits,
            name: 'RSA-OAEP',
            publicExponent: this.cryptoParameters.RSAExponent
          },
          true,
          ['encrypt']
        );

        publicKeyCrypto.toPem = function toPem() {
          return new Promise((resolve, reject) => {
            crypto.subtle.exportKey('spki', this).then((pubkeySpki) => {
              const payload = btoa(
                String.fromCharCode.apply(null, new Uint8Array(pubkeySpki))
              );
              const pem = `-----BEGIN PUBLIC KEY-----\n${payload}\n-----END PUBLIC KEY-----`;
              resolve(pem);
            }, reject);
          });
        };

        const privateKeyCrypto = await crypto.subtle.importKey(
          format,
          {
            d: b16to64url(privateKey.d.toString(16)),
            dp: b16to64url(privateKey.dP.toString(16)),
            dq: b16to64url(privateKey.dQ.toString(16)),
            e,
            kty: 'RSA',
            n,
            p: b16to64url(privateKey.p.toString(16)),
            q: b16to64url(privateKey.q.toString(16)),
            qi: b16to64url(privateKey.qInv.toString(16))
          },
          {
            hash: { name: 'SHA-256' },
            name: 'RSA-OAEP'
          },
          false,
          ['decrypt']
        );

        this.publicKey = publicKeyCrypto;
        this.privateKey = privateKeyCrypto;

        return {
          privateKey: privateKeyCrypto,
          publicKey: publicKeyCrypto
        };
      } else {
        throw new Error('missing site salt and/or cryto parameters');
      }
    } catch (error) {
      console.error(
        'helper.encryption.Hspii.generateRSAKeys: ' + error.message
      );
      throw new Error('error while generating the rsa keys');
    }
  }

  async decrypt(encryptedContent, salt, key = null) {
    try {
      // Get the private key
      if (!key) {
        if (this.privateKey) {
          key = this.privateKey;
        } else {
          key = await getKey();
          if (key) {
            this.privateKey = key;
          } else {
            throw new Error('Decryption needs a private key');
          }
        }
      }

      // Decrypt
      const value = await crypto.subtle.decrypt(
        {
          name: 'RSA-OAEP'
        },
        key,
        Uint8Array.from(atob(encryptedContent), (c) => c.charCodeAt(0))
      );
      const payload = new TextDecoder().decode(new Uint8Array(value));

      // Strip salt
      const plaintext = payload.startsWith(salt)
        ? payload.slice(salt.length)
        : payload;

      return plaintext;
    } catch (error) {
      throw error;
    }
  }

  async getPrivateKey() {
    return getKey();
  }

  async savePrivateKey() {
    if (this.privateKey) {
      await storeKey(this.privateKey);
    }
  }

  async deletePrivateKey() {
    await removeKey();
  }
}

export default Hspii;
